import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Collapse, Grid, Grow, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Typography } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { commonSort, getTitle } from "../../common";
import AddButton from "../../components/AddButton/AddButton";
import SearchBar from "../../components/SearchBar/SearchBar";
import TableButtons from "../../components/Button/TableButtons";
import LoadingBackdrop from "../../components/LoadingBackdrop/LoadingBackdrop";
import AddShiftPlanPopup from "../../components/Popup/AddShiftPlanPopup";
import Modal from "../../components/Modal/Modal";
import { StyledTableDiv } from "../../components/StyledComponents/StyledComponents";
import moment from "moment";
import { getShiftPlans, addShiftPlan, updateShiftPlan, deleteShiftPlan } from "../../store/actions/ShiftPlansActions";

const hoursToDate = (hourString) => {
    const date = new Date();
    const [hour, minute] = hourString.split(":");
    date.setHours(hour, minute);
    return date;
};

export default function ShiftPlans() {
    const { t } = useTranslation("translation");

    const dispatch = useDispatch();
    const [initialRows, setInitialRows] = useState([]);
    let [rows, setRows] = useState([]);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [maxEndDate, setMaxEndDate] = useState(new Date("1900-01-01"));

    const { ShiftPlans } = useSelector((ShiftPlans) => ShiftPlans);

    useEffect(() => {
        loadHandler();
    }, []);

    useEffect(() => {
        if (ShiftPlans?.shiftPlans?.resultObject) {
            let shiftPlans = ShiftPlans.shiftPlans.resultObject;
            shiftPlans = shiftPlans.map((shiftPlan) => {
                shiftPlan.shifts = shiftPlan.shifts.map((shift) => {
                    shift.startingHour = hoursToDate(shift.startingHour);
                    shift.endingHour = hoursToDate(shift.endingHour);
                    return shift;
                });

                if (moment(shiftPlan.endDate).isAfter(maxEndDate)) {
                    setMaxEndDate(moment(shiftPlan.endDate).add(1, "days"));
                }
                return shiftPlan;
            });

            setInitialRows(shiftPlans);
            setRows(shiftPlans);
            ShiftPlans.shiftPlans = {};
            setPageLoaded(true);
        }
        if (ShiftPlans?.isTaskUpdated) {
            setMaxEndDate(new Date("1900-01-01"));
            loadHandler();
        }
    }, [ShiftPlans]);

    const loadHandler = () => {
        dispatch(getShiftPlans());
    };

    // Update and Add
    const updateAddHandler = (data, isAdd) => {
        const newShifts = data.shifts.map((shift, index) => {
            return {
                id: shift.id,
                shiftNo: (index + 1),
                startingHour: moment(shift.startingHour).format("HH:mm:ss"),
                endingHour: moment(shift.endingHour).format("HH:mm:ss"),
            };
        });
        data = { ...data, shifts: newShifts };

        if (isAdd) {
            data.shifts.forEach((shift) => {
                shift.id = 0;
            });
            dispatch(addShiftPlan(data));
        } else {
            dispatch(updateShiftPlan(data));
        }

        setShiftPlanRow(null);
        setIsShiftPlanPopupOpen(false);
    };

    // Delete row
    const deleteHandler = (row) => {
        dispatch(deleteShiftPlan(row.id));
        toggleModal();
    };

    // Filter table
    const [filterText, setFilterText] = useState("");
    const filterData = (value) => {
        setFilterText(value);
        const newFilteredText = value.toLowerCase();
        let filteredItems = cloneObject(initialRows).filter((item) => item.id?.toString().includes(newFilteredText) || item.name?.toLowerCase().includes(newFilteredText));
        setRows(filteredItems);
    };

    useEffect(() => {
        filterData(filterText);
    }, [initialRows]);

    // Sort function
    let [sortOrder, setSortOrder] = useState();
    let [sortColumn, setSortColumn] = useState();
    const handleSort = (row, column) => {
        const sortedList = commonSort(row, column);
        setSortOrder(sortedList[0]);
        setSortColumn(sortedList[1]);
        setInitialRows(sortedList[2]);
    };

    // Pagination
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const handleChangePage = (_event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setCurrentPage(0);
    };

    const cloneObject = (obj) => {
        return JSON.parse(JSON.stringify(obj));
    };

    // Delete Modal
    const [isModalOpen, setModalOpen] = useState(false);
    const [deletedRow, setDeletedRow] = useState([]);
    const deleteButtonHandler = (row) => {
        setDeletedRow(row);
        toggleModal();
    };

    const toggleModal = () => {
        setModalOpen((prevState) => !prevState);
    };

    // Expand row
    const [openRow, setOpenRow] = useState(false);
    const [expandID, setExpandId] = useState("");
    const expandRow = (rowId) => {
        if (openRow && expandID !== rowId) {
            setExpandId(rowId);
        } else {
            setOpenRow(!openRow);
            setExpandId(rowId);
        }
    };

    // Shift Plan Popup
    const [isShiftPlanPopupOpen, setIsShiftPlanPopupOpen] = useState(false);
    const [shiftPlanRow, setShiftPlanRow] = useState(null);

    const handleShiftPlanPopupOpen = (row) => {
        setShiftPlanRow(row);
        setIsShiftPlanPopupOpen(true);
    };

    const handleShiftPlanPopupClose = () => {
        setShiftPlanRow(null);
        setIsShiftPlanPopupOpen(false);
    };

    const renderTableCell = (rowItems, isSortingAction = true) => {
        const directionType = sortOrder === 1 ? "asc" : "desc";
        return rowItems.map((item, index) => {
            if (item.id === "Actions") {
                return (
                    <TableCell id={`sp_rowItems_TableCell_Actions_${index}_${item.id}`} align="center">
                        {t("Actions")}
                    </TableCell>
                );
            } else if (item.id === "") {
                return <TableCell id={`sp_rowItems_TableCell_${index}_${item.id}`} align="center"></TableCell>;
            } else {
                return isSortingAction ? (
                    <TableCell id={`sp_rowItems_TableCell_${index}_${item.id}`} align="center">
                        <TableSortLabel
                            id={`sp_rowItems_TableSortLabel_${index}_${item.id}`}
                            active={sortColumn === item.id}
                            direction={directionType}
                            onClick={() => handleSort(cloneObject(initialRows), item.id)}
                            style={{ paddingLeft: 25 }}
                        >
                            {item.label}
                        </TableSortLabel>
                    </TableCell>
                ) : (
                    <TableCell align="center">{item.label}</TableCell>
                );
            }
        });
    };

    return (
        <StyledTableDiv>
            {getTitle(t("ShiftPlans"))}
            <AddButton onClick={() => setIsShiftPlanPopupOpen(true)} />
            {pageLoaded === true ? (
                <Grid container spacing={4} alignItems="center" justifyContent="center" sx={{ marginBottom: 4 }}>
                    <Grid item xs={12} sm={12} md={4}>
                        <SearchBar value={filterText} onChange={(newValue) => setFilterText(newValue)} onRequestSearch={(value) => filterData(value)} />
                    </Grid>
                </Grid>
            ) : null}
            <Box m={2} />
            {rows.length > 0 ? (
                <Grow in={true} style={{ transformOrigin: "0 0 0" }} {...{ timeout: 1000 }}>
                    <TableContainer id="sp_TableContainer" component={Paper}>
                        <Table id="sp_Table" aria-label="simple table">
                            <TableHead id="sp_TableHead">
                                <TableRow id="sp_TableRow">
                                    {renderTableCell([
                                        { id: "", label: "" },
                                        { id: "id", label: t("ID") },
                                        { id: "name", label: t("Name") },
                                        { id: "startDate", label: t("StartDate") },
                                        { id: "endDate", label: t("EndDate") },
                                        { id: "Actions", label: t("Actions") },
                                    ])}
                                </TableRow>
                            </TableHead>
                            <TableBody id="sp_TableBody">
                                {rows.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage).map((row, rowIndex) => (
                                    <React.Fragment key={row.id}>
                                        <TableRow id={`sp_TableRow_${rowIndex}`}>
                                            <TableCell id={`sp_TableCell_expand`}>
                                                <IconButton aria-label="expand row" size="small" onClick={() => expandRow(row.id)}>
                                                    {openRow && expandID === row.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell id={`sp_TableCell_${rowIndex}`} align="center">
                                                {row.id}
                                            </TableCell>
                                            <TableCell id={`sp_TableCell_1_${rowIndex}`} align="center">
                                                {row.name}
                                            </TableCell>
                                            <TableCell id={`sp_TableCell_2_${rowIndex}`} align="center">
                                                {moment(row.startDate).format("YYYY-MM-DD")}
                                            </TableCell>
                                            <TableCell id={`sp_TableCell_3_${rowIndex}`} align="center">
                                                {moment(row.endDate).format("YYYY-MM-DD")}
                                            </TableCell>
                                            <TableButtons row={row} enableEditHandler={() => handleShiftPlanPopupOpen(row)} deleteButtonHandler={() => deleteButtonHandler(row)} detail={false} />
                                        </TableRow>
                                        <TableRow>
                                            {expandID === row.id ? (
                                                <TableCell id={`sp_TableCell_4_subList`} style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                                    <Collapse id={`sp_Collapse_1_${rowIndex}`} in={openRow} timeout="auto" unmountOnExit>
                                                        <Box id={`sp_Box_1_${rowIndex}`} margin={1}>
                                                            <Typography id={`sp_Typography_1_${rowIndex}`} variant="h6" gutterBottom component="div">
                                                                {t("Shifts")}
                                                            </Typography>
                                                            <Table id={`sp_Table_1_${rowIndex}`} size="small">
                                                                <TableHead id={`sp_TableHead_1_${rowIndex}`}>
                                                                    <TableRow id={`sp_TableRow_2_${rowIndex}`}>
                                                                        {renderTableCell(
                                                                            [
                                                                                { id: "name", label: t("Name") },
                                                                                { id: "startingHour", label: t("Start") },
                                                                                { id: "endingHour", label: t("End") },
                                                                            ],
                                                                            false
                                                                        )}
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody id={`sp_TableBody_1_${rowIndex}`}>
                                                                    {row.shifts.map((detailRow, itemIndex) => (
                                                                        <TableRow id={`sp_TableRow_3_${rowIndex}_${itemIndex}`} key={detailRow.id}>
                                                                            <TableCell id={`sp_TableCell_subName`} align="center">
                                                                                {t("Shift") + " " + (itemIndex + 1)}
                                                                            </TableCell>
                                                                            <TableCell id={`sp_TableCell_substartingHour`} align="center">
                                                                                {moment(detailRow.startingHour).format("HH:mm")}
                                                                            </TableCell>
                                                                            <TableCell id={`sp_TableCell_subendingHour`} align="center">
                                                                                {moment(detailRow.endingHour).format("HH:mm")}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            ) : null}
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            id="sp_TablePagination"
                            rowsPerPageOptions={[20, 50, 100]}
                            component="div"
                            count={rows.length}
                            rowsPerPage={rowsPerPage}
                            page={currentPage}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </TableContainer>
                </Grow>
            ) : null}

            <Modal title={t("Delete")} content={t("WillYouDoTransaction")} approved={() => deleteHandler(deletedRow)} isModalOpen={isModalOpen} toggle={toggleModal} />

            {isShiftPlanPopupOpen ? (
                <AddShiftPlanPopup open={isShiftPlanPopupOpen} shiftPlan={shiftPlanRow} defaultStartDate={maxEndDate} onAdd={updateAddHandler} onCancel={handleShiftPlanPopupClose} />
            ) : null}

            <LoadingBackdrop id="sp_lbd" />
        </StyledTableDiv>
    );
}
